/**
 * RecentPerformancesView - Displays recent match performances chart
 *
 * Shows bar chart of player performance ratings across recent games
 * with filter options and status indicators. On mobile, persistent
 * rating labels appear above each bar since hover tooltips are
 * unavailable on touch devices.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

import ChartLegend from "@/features/player-detail/components/molecules/ChartLegend";
import FilterDropdown from "@/features/player-detail/components/molecules/FilterDropdown";
import PerformanceBar from "@/features/player-detail/components/molecules/PerformanceBar";
import SelectedMatchInfo from "@/features/player-detail/components/molecules/SelectedMatchInfo";
import type {
  MatchStatus,
  PerformanceFilter,
} from "@/features/player-detail/types";

/**
 * Performance data with formatted date for display
 */
interface DisplayPerformance {
  readonly id: string;
  readonly matchDate: string;
  readonly formattedDate: string;
  readonly opponent: string;
  readonly status: MatchStatus;
  readonly rating: number;
  readonly minutes: number;
}

/**
 * Props for RecentPerformancesView component
 */
interface RecentPerformancesViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether data is loading */
  readonly isLoading: boolean;
  /** Whether there is data to display */
  readonly hasData: boolean;
  /** Match performances to display */
  readonly performances: readonly DisplayPerformance[];
  /** Currently active filter */
  readonly activeFilter: PerformanceFilter;
  /** Handler for filter change */
  readonly onFilterChange: (filter: PerformanceFilter) => void;
  /** Currently hovered bar index */
  readonly hoveredIndex: number | null;
  /** Currently selected/hovered performance data for info display */
  readonly selectedPerformance: DisplayPerformance | null;
  /** Handler for hover change */
  readonly onHoverChange: (index: number | null) => void;
  /** Handler for mobile bar tap — toggles tooltip */
  readonly onPressBar: (index: number) => void;
  /** Whether the screen is mobile sized — enables persistent bar labels */
  readonly isSmallScreen: boolean;
}

/** Y-axis labels for the chart (0-10 scale) */
const Y_AXIS_LABELS = [10, 8, 6, 4, 2, 0] as const;

/**
 * RecentPerformancesView renders the recent performances chart
 * @param props - View props
 * @param props.isDark - Whether dark mode is active
 * @param props.isLoading - Whether data is loading
 * @param props.hasData - Whether there is data to display
 * @param props.performances - Match performances to display
 * @param props.activeFilter - Currently active filter
 * @param props.onFilterChange - Handler for filter change
 * @param props.hoveredIndex - Currently hovered bar index
 * @param props.selectedPerformance - Currently selected/hovered performance data
 * @param props.onHoverChange - Handler for hover change
 * @param props.onPressBar - Handler for mobile bar tap
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @returns Box containing performance chart with filters
 */
const RecentPerformancesView = ({
  isDark,
  isLoading,
  hasData,
  performances,
  activeFilter,
  onFilterChange,
  hoveredIndex,
  selectedPerformance,
  onHoverChange,
  onPressBar,
  isSmallScreen,
}: RecentPerformancesViewProps) => (
  <Box
    {...{
      testID: TEST_ID_PLAYER_DETAIL.RECENT_PERFORMANCES,
      "data-testid": TEST_ID_PLAYER_DETAIL.RECENT_PERFORMANCES,
      accessibilityLabel: "Recent match performance ratings",
      "aria-label": "Recent match performance ratings",
    }}
    className="w-full overflow-hidden rounded-sm border border-outline-card bg-surface-card p-4"
  >
    <Stack className="gap-3">
      {/* Header */}
      <Stack direction="horizontal" className="items-center justify-between">
        <Text
          variant="body-strong"
          className="uppercase tracking-wide text-content-primary"
        >
          Recent Performances
        </Text>
        <FilterDropdown
          isDark={isDark}
          activeFilter={activeFilter}
          onSelect={onFilterChange}
        />
      </Stack>

      {/* Chart area */}
      {isLoading ? (
        <Box className="h-48 w-full items-center justify-center">
          <Text variant="body" className="text-content-muted">
            Loading performances...
          </Text>
        </Box>
      ) : !hasData ? (
        <Box className="h-48 w-full items-center justify-center">
          <Text variant="body" className="text-content-muted">
            No match data available
          </Text>
        </Box>
      ) : (
        <>
          {/* Selected match info — always reserves height to prevent layout shift */}
          <SelectedMatchInfo
            selectedPerformance={selectedPerformance}
            isDark={isDark}
          />

          <Box className="relative h-44 w-full overflow-hidden">
            {/* Y-axis labels */}
            <Stack className="absolute inset-y-0 left-0 w-5 justify-between">
              {Y_AXIS_LABELS.map(label => (
                <Text
                  key={label}
                  {...{
                    testID: `${TEST_ID_PLAYER_DETAIL.AXIS_LABEL_Y}-${label}`,
                    "data-testid": `${TEST_ID_PLAYER_DETAIL.AXIS_LABEL_Y}-${label}`,
                  }}
                  variant="caption"
                  className="text-content-muted"
                >
                  {label}
                </Text>
              ))}
            </Stack>

            {/* Bars - no bottom padding since labels are outside */}
            <Stack
              direction="horizontal"
              className="h-full flex-1 items-end justify-between gap-1 pl-6"
            >
              {[...performances].reverse().map((perf, index) => (
                <PerformanceBar
                  key={perf.id}
                  perf={perf}
                  isHovered={hoveredIndex === index}
                  isAnyBarHovered={hoveredIndex !== null}
                  isDark={isDark}
                  isSmallScreen={isSmallScreen}
                  index={index}
                  onHoverChange={onHoverChange}
                  onPressBar={onPressBar}
                />
              ))}
            </Stack>
          </Box>

          {/* Date labels - completely outside chart layout */}
          <Stack
            direction="horizontal"
            className="justify-between gap-1 py-1 pl-6"
          >
            {[...performances].reverse().map(perf => (
              <Box
                key={`date-${perf.id}`}
                className={
                  isSmallScreen
                    ? "flex-1 -rotate-45 items-center pt-2"
                    : "flex-1 items-center pt-px"
                }
              >
                <Text
                  variant="micro"
                  className="w-16 text-center text-content-muted"
                >
                  {perf.formattedDate}
                </Text>
              </Box>
            ))}
          </Stack>
        </>
      )}

      {/* Legend */}
      <ChartLegend isDark={isDark} />
    </Stack>
  </Box>
);

RecentPerformancesView.displayName = "RecentPerformancesView";

export default memo(RecentPerformancesView);
